import { useState } from 'react';
import { LINE_COLORS, LINE_WEIGHTS } from './constants';

const BUTTON_3D_BASE = {
  transformStyle: 'preserve-3d',
  transform: 'translateZ(0)',
  transition: 'transform 180ms cubic-bezier(.22,1,.36,1), box-shadow 180ms ease',
};

function threeDButton(base, isLight, hover, pressed) {
  const depth = pressed ? 1 : hover ? 4 : 2;
  const offset = pressed ? 0 : hover ? 4 : 2;
  const shadowTop = isLight
    ? 'rgba(255,255,255,0.9)'
    : 'rgba(255,255,255,0.12)';
  const shadowBottom = isLight
    ? 'rgba(15,23,42,0.22)'
    : 'rgba(0,0,0,0.65)';
  return {
    ...base,
    transform: `translateZ(0) translateY(-${offset}px) ${pressed ? 'scale(0.97)' : hover ? 'scale(1.03)' : ''}`,
    boxShadow: `inset 0 1px 0 ${shadowTop}, 0 ${depth}px 0 ${shadowBottom}, 0 ${
      depth + 6
    }px 14px -6px ${isLight ? 'rgba(15,23,42,0.28)' : 'rgba(0,0,0,0.55)'}`,
  };
}

export default function Toolbar({
  isDrawing,
  selectedColor,
  selectedWeight,
  currentTag,
  hasCurrentPoints,
  onToggleDrawing,
  onColorChange,
  onWeightChange,
  onTagChange,
  onFinishLine,
  onCancelLine,
  onUndo,
  isLight = false,
  C = null,
}) {
  const theme = C || (isLight
    ? {
        accent: '#2563eb',
        accentSoft: 'rgba(37, 99, 235, 0.75)',
        accentBg: 'rgba(37, 99, 235, 0.10)',
        accentBgHover: 'rgba(37, 99, 235, 0.14)',
        accentOnBg: '#ffffff',
        glassBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.8) 50%, rgba(239, 246, 255, 0.85) 100%)',
        glassBorder: 'rgba(37, 99, 235, 0.18)',
        glassShadow: '0 4px 16px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        textPrimary: '#0f172a',
        textSecondary: 'rgba(51, 65, 85, 0.75)',
        textMuted: 'rgba(100, 116, 139, 0.65)',
        inputBg: 'rgba(255, 255, 255, 0.9)',
        inputBorder: 'rgba(148, 163, 184, 0.30)',
        btnGhostBg: 'rgba(15, 23, 42, 0.05)',
        btnGhostBgHover: 'rgba(15, 23, 42, 0.09)',
        btnGhostBorder: 'rgba(15, 23, 42, 0.10)',
      }
    : {
        accent: '#facc15',
        accentSoft: 'rgba(250, 204, 21, 0.7)',
        accentBg: 'rgba(250, 204, 21, 0.12)',
        accentBgHover: 'rgba(250, 204, 21, 0.16)',
        accentOnBg: '#0f172a',
        glassBg: 'linear-gradient(135deg, rgba(30, 58, 138, 0.55) 0%, rgba(23, 37, 84, 0.5) 50%, rgba(30, 64, 175, 0.55) 100%)',
        glassBorder: 'rgba(250, 204, 21, 0.18)',
        glassShadow: '0 8px 32px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
        textPrimary: '#ffffff',
        textSecondary: 'rgba(226, 232, 240, 0.7)',
        textMuted: 'rgba(148, 163, 184, 0.6)',
        inputBg: 'rgba(255, 255, 255, 0.10)',
        inputBorder: 'rgba(255, 255, 255, 0.15)',
        btnGhostBg: 'rgba(255, 255, 255, 0.10)',
        btnGhostBgHover: 'rgba(255, 255, 255, 0.15)',
        btnGhostBorder: 'rgba(255, 255, 255, 0.15)',
      });

  const glassCardStyle = {
    background: theme.glassBg,
    border: `1px solid ${theme.glassBorder}`,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: theme.glassShadow,
  };

  return (
    <div
      className="absolute top-4 left-4 z-[500] flex flex-col gap-3 slide-in"
      style={{ maxHeight: 'calc(100% - 2rem)', overflowY: 'auto' }}
    >
      <div className="rounded-xl p-4 w-72" style={glassCardStyle}>
        <h2 className="font-bold text-lg mb-3 flex items-center gap-2" style={{ color: theme.accent }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Drawing Tools
        </h2>

        <ToggleDrawingButton
          isDrawing={isDrawing}
          onToggle={onToggleDrawing}
          isLight={isLight}
          theme={theme}
        />

        {isDrawing && (
          <p className="text-xs mt-2 text-center" style={{ color: theme.textSecondary }}>
            Click on the map to add points to your line
          </p>
        )}
      </div>

      <div className="rounded-xl p-4 w-72 fade-in" style={glassCardStyle}>
        <h3 className="font-semibold text-sm mb-3 uppercase tracking-wider" style={{ color: theme.accentSoft }}>
          Line Color
        </h3>
        <div className="grid grid-cols-5 gap-2">
          {LINE_COLORS.map((c) => (
            <ColorSwatchButton
              key={c.value}
              color={c}
              selected={selectedColor === c.value}
              onClick={() => onColorChange(c.value)}
              isLight={isLight}
              theme={theme}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl p-4 w-72 fade-in" style={glassCardStyle}>
        <h3 className="font-semibold text-sm mb-3 uppercase tracking-wider" style={{ color: theme.accentSoft }}>
          Line Width
        </h3>
        <div className="flex gap-2">
          {LINE_WEIGHTS.map((w) => (
            <WidthButton
              key={w}
              weight={w}
              selected={selectedWeight === w}
              color={selectedColor}
              onClick={() => onWeightChange(w)}
              isLight={isLight}
              theme={theme}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl p-4 w-72 fade-in" style={glassCardStyle}>
        <h3 className="font-semibold text-sm mb-3 uppercase tracking-wider" style={{ color: theme.accentSoft }}>
          Line Tag / Label
        </h3>
        <input
          type="text"
          data-testid="mappoint-tag-input"
          value={currentTag}
          onChange={(e) => onTagChange(e.target.value)}
          placeholder="Enter a tag for your line..."
          className="w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none transition-colors"
          style={{
            backgroundColor: theme.inputBg,
            border: `1px solid ${theme.inputBorder}`,
            color: theme.textPrimary,
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = theme.accent + '99')}
          onBlur={(e) => (e.currentTarget.style.borderColor = theme.inputBorder)}
        />
      </div>

      {hasCurrentPoints && (
        <div className="rounded-xl p-4 w-72 fade-in" style={glassCardStyle}>
          <div className="flex gap-2">
            <SaveLineButton onClick={onFinishLine} isLight={isLight} theme={theme} />
            <UndoButton onClick={onUndo} isLight={isLight} theme={theme} />
            <CancelButton onClick={onCancelLine} isLight={isLight} theme={theme} />
          </div>
        </div>
      )}
    </div>
  );
}

function ToggleDrawingButton({ isDrawing, onToggle, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const isActive = isDrawing;
  const bg = isActive
    ? theme.accent + 'cc'
    : hover
      ? theme.btnGhostBgHover
      : theme.btnGhostBg;
  const color = isActive
    ? theme.accentOnBg
    : theme.accentSoft;
  const border = isActive
    ? 'transparent'
    : theme.btnGhostBorder;
  return (
    <button
      data-testid="mappoint-toggle-drawing"
      onClick={onToggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative w-full py-2.5 px-4 rounded-lg font-semibold text-sm border flex items-center justify-center gap-2"
      style={{
        backgroundColor: bg,
        color,
        borderColor: border,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || isActive, pressed),
      }}
    >
      {isDrawing ? (
        <>
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: theme.accentOnBg }}
          />
          Drawing Mode ON
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Start Drawing
        </>
      )}
    </button>
  );
}

function ColorSwatchButton({ color, selected, onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const borderColor = selected ? theme.accent : theme.btnGhostBorder;
  const ring = selected ? `0 0 0 3px ${theme.accentBg}` : undefined;
  return (
    <button
      title={color.name}
      data-testid={`mappoint-color-${color.name.toLowerCase()}`}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative w-10 h-10 rounded-lg border-2 flex items-center justify-center"
      style={{
        backgroundColor: color.value,
        borderColor,
        transform: selected ? 'scale(1.1)' : hover ? 'scale(1.1)' : undefined,
        boxShadow: ring,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || selected, pressed),
      }}
    >
      {selected && (
        <svg
          className="w-4 h-4 mx-auto"
          fill="none"
          stroke={color.value === '#ffffff' || color.value === '#facc15' ? '#1e3a8a' : '#fff'}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

function WidthButton({ weight, selected, color, onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = selected ? theme.accentBg : hover ? theme.btnGhostBgHover : theme.btnGhostBg;
  const border = selected ? theme.accent + 'b3' : theme.btnGhostBorder;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 py-2 rounded-lg transition-all duration-150 flex items-center justify-center border-2"
      style={{
        backgroundColor: bg,
        borderColor: border,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover || selected, pressed),
      }}
    >
      <div
        className="rounded-full"
        style={{ height: `${weight}px`, width: '24px', backgroundColor: color }}
      />
    </button>
  );
}

function SaveLineButton({ onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      data-testid="mappoint-save-line"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5 border border-transparent"
      style={{
        backgroundColor: theme.accent + 'cc',
        color: theme.accentOnBg,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      Save Line
    </button>
  );
}

function UndoButton({ onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      title="Undo last point"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative px-3 py-2.5 rounded-lg font-semibold text-sm border"
      style={{
        backgroundColor: hover ? theme.btnGhostBgHover : theme.btnGhostBg,
        color: theme.accentSoft,
        borderColor: theme.btnGhostBorder,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a4 4 0 014 4v2M3 10l4-4M3 10l4 4" />
      </svg>
    </button>
  );
}

function CancelButton({ onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = isLight
    ? (hover ? 'rgba(239,68,68,0.14)' : 'rgba(239, 68, 68, 0.08)')
    : (hover ? 'rgba(239,68,68,0.25)' : 'rgba(239, 68, 68, 0.15)');
  const color = isLight ? '#dc2626' : '#fca5a5';
  const border = isLight ? 'rgba(239, 68, 68, 0.20)' : 'rgba(248, 113, 113, 0.20)';
  return (
    <button
      onClick={onClick}
      title="Cancel drawing"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative px-3 py-2.5 rounded-lg font-semibold text-sm border"
      style={{
        backgroundColor: bg,
        color,
        borderColor: border,
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}
